/**
 * エアコンクリーニング購入ページ（sections/aicon-cleaning-order.liquid）。
 * 2026-09-04 嵐さんデザイン「開発渡し_クリーニングページ」の script を移したもの。
 *
 * ★ バリアントIDを焼かない ★
 *   Liquid が `data-variants` に (id, title, price) を渡す。突き合わせは
 *   マークアップの `data-variant-*` に書いたバリアント名の文字列で、
 *   backend の cleaningVariantPrices() が作る名前と一致させている。
 *   ズレたらボタンを押せなくして、黙って違う額で入らないようにする。
 *
 * ★ 金額は必ずバリアント価格から出す ★
 *   ページの数字（プランの単価・オプションの単価・割引額・例の計算・合計・追従バー）を
 *   すべてここで埋める。テーマに数値を書くと料金改定でページとカートの額が食い違う。
 *
 * ★ Shopify の価格は最小通貨単位（円は100倍）★
 *   `variant.price` は 9,980円 なら `998000`。割り忘れると **合計 ¥998,000** になる
 *   （2026-09-03 に本番で発生）。scripts/check-variant-price-yen.mjs が
 *   「/ 100 があるか」を CI で見ている。
 *
 * ★ 割引は「引いた額」ではなく「引いた結果」から逆算する ★
 *   渡し版は `2000 * (台数-1)` を直接引いていた。2,000 はいま
 *   pricing_configs の second_unit_discount と一致しているが、テーマに数値を持つと
 *   改定で必ず取り残される。∴ 表示する割引額は
 *     （定価で並べた合計） − （実際にカートへ入れる行の合計）
 *   から出す。**構造的に画面の合計とカートの請求額が一致する**。
 *
 * ★ 1台目に選ぶのは「実効割引がいちばん大きい種類」★
 *   合計 = Σ(2台目価格 × 台数) + (1台目に選んだ種類の 1台目価格 − 2台目価格)。
 *   ∴ 1台目に選ぶ種類の**実効割引が大きいほど合計は高くなる**。
 *
 *   割引額はいま「なし」「つき」で同じ1本の設定（second_unit_discount）なので
 *   どちらを選んでも同額だが、**同額であることに依存しない**。
 *   `secondUnitPrice()` は「割引が本体価格以上なら割引しない」でクランプするため、
 *   例えば割引 12,000円 だと なし（9,980円）は割引0・つき（17,800円）は 12,000円 と
 *   **種類ごとに実効割引が変わる**。そのとき「なし」を1台目にすると
 *   店が 12,000円 取りこぼす。大きい方を選べば取りこぼしが構造的に起きない。
 *   （逆に、種類ごとに1台目を1つずつ入れると割引が1台ぶん減って過大請求になる）
 *
 * ★ オプションのバリアントが無いストアでは、その行を隠す ★
 *   オプション3本（室外機クリーニング / 特殊機種 / 高所作業）は 2026-09-04 に
 *   足したもので、migration → backend → Store Sync が済むまで商品に存在しない。
 *   出したまま押せないと「選べるのに何も起きない」になるので消す。
 */
(function () {
  'use strict';

  /** 台数の上限（渡し版と同じ） */
  var MAX_QTY = 9;

  /** 円表示。Shopify の価格は最小通貨単位なので 100 で割る */
  function yen(amountInCents) {
    return Math.round(Number(amountInCents) / 100).toLocaleString('ja-JP');
  }

  function yenWithUnit(amountInCents) {
    return yen(amountInCents) + '円';
  }

  function parseVariants(root) {
    try {
      var list = JSON.parse(root.getAttribute('data-variants') || '[]');
      var byTitle = {};
      list.forEach(function (v) { byTitle[v.title] = v; });
      return byTitle;
    } catch (err) {
      console.error('[cleaning] バリアントの読み取りに失敗しました:', err);
      return {};
    }
  }

  function init(root) {
    // ★ 二重初期化を止める ★
    //   `shopify:section:load`（テーマエディタ）で boot() が走ると、
    //   すでに初期化済みのセクションにも listener を足してしまい、
    //   ＋ボタン1回で台数が2つ増える。
    if (root.dataset.cleaningInit === '1') return;
    root.dataset.cleaningInit = '1';

    var byTitle = parseVariants(root);

    var plans = Array.prototype.slice.call(root.querySelectorAll('.kc-plan'));
    // マークアップは固定だが、空だと以降が全部 TypeError で死ぬので先に降りる
    if (plans.length === 0) {
      console.error('[cleaning] プランの要素が見つかりません');
      return;
    }
    var opts = Array.prototype.slice.call(root.querySelectorAll('.kc-opt'));
    var rowsEl = root.querySelector('#kc-rows');
    var totalEl = root.querySelector('#kc-total');
    var cbtn = root.querySelector('#kc-cbtn');
    var clearBtn = root.querySelector('#kc-clear');
    var errEl = root.querySelector('#kc-cerr');
    var barLabel = root.querySelector('#kc-bar-label');
    var barPrice = root.querySelector('#kc-bar-price');
    var barBtn = root.querySelector('#kc-bar-btn');
    var modal = root.querySelector('#kc-modal');
    var agreeBtn = root.querySelector('#kc-agreego');
    var closeBtn = root.querySelector('#kc-mclose');

    if (!rowsEl || !totalEl || !cbtn) return;

    /* ── バリアントを引く ───────────────────────────────── */

    /** 各プランの (1台目, 2台目以降) */
    plans.forEach(function (plan) {
      plan.__first = byTitle[plan.getAttribute('data-variant-first')] || null;
      plan.__second = byTitle[plan.getAttribute('data-variant-second')] || null;
    });

    var missingPlans = plans.filter(function (p) { return !p.__first; });
    if (missingPlans.length > 0) {
      // ★ 黙って別の額で入らないようにする ★ 名前がズレたらここで止まる
      console.error(
        '[cleaning] プランのバリアント名が一致しません:',
        missingPlans.map(function (p) { return p.getAttribute('data-variant-first'); }),
        '／ストアにあるのは:',
        Object.keys(byTitle),
      );
      errEl.textContent = 'ただいま選択できません。お手数ですがお問い合わせください。';
      errEl.hidden = false;
      cbtn.setAttribute('aria-disabled', 'true');
      /*
       * ★ 追従バーごと消す ★
       *   ここを通ると render() が一度も走らないので、バーは金額が `—` のまま
       *   「エアコンクリーニング —」と出続ける。押せないものを出しておかない。
       */
      var brokenBar = root.querySelector('#kc-bar');
      if (brokenBar) brokenBar.hidden = true;
      return;
    }

    /**
     * オプションは無ければ行ごと消す。
     * 商品同期の前は3本のバリアントが存在しないため。
     */
    opts = opts.filter(function (opt) {
      var variant = byTitle[opt.getAttribute('data-variant')];
      if (!variant) {
        console.info(
          '[cleaning] オプションのバリアントがまだありません（Store Sync 前）:',
          opt.getAttribute('data-variant'),
        );
        opt.remove();
        return false;
      }
      opt.__variant = variant;
      return true;
    });
    // 3本とも無ければ STEP 2 のかたまりごと隠す（空の枠だけ残さない）
    var optsWrap = root.querySelector('.kc-opts');
    if (optsWrap && opts.length === 0) {
      var step2 = optsWrap.previousElementSibling;
      optsWrap.hidden = true;
      if (step2 && step2.classList.contains('kc-optnote')) {
        step2.hidden = true;
        var step2head = step2.previousElementSibling;
        if (step2head && step2head.classList.contains('kc-step')) step2head.hidden = true;
      }
    }

    /* ── 固定の数字を埋める ─────────────────────────────── */

    var normalPlan = plans[0];
    var robotPlan = plans[1] || null;

    function setPriceText(key, text) {
      var nodes = root.querySelectorAll('[data-cleaning-price="' + key + '"]');
      for (var i = 0; i < nodes.length; i++) nodes[i].textContent = text;
    }

    setPriceText('normal', yen(normalPlan.__first.price));
    if (robotPlan) setPriceText('robot', yen(robotPlan.__first.price));

    var offband = root.querySelector('.kc-offband');
    var normalSecond = normalPlan.__second ? Number(normalPlan.__second.price) : null;
    var normalFirst = Number(normalPlan.__first.price);
    if (normalSecond !== null && normalFirst - normalSecond > 0) {
      setPriceText('discount', yen(normalFirst - normalSecond));
      setPriceText('normal-second', yen(normalSecond));
      setPriceText('normal-two-units', yen(normalFirst + normalSecond));
    } else {
      /*
       * 2台目以降のバリアントが無いストアと、割引が 0円 のストア。
       * 0円 のときに出すと「2台目以降 1台につき 0円OFF」「例) 9,980円+9,980円=19,960円」
       * という**得に見えない訴求**が出る。`secondUnitPrice()` が
       * 「割引が本体価格以上なら割引しない」でクランプすると実際に起きる。
       */
      if (offband) offband.hidden = true;
      console.info('[cleaning] 2台目以降の割引がありません。割引の案内を隠しました');
    }

    opts.forEach(function (opt) {
      setPriceText(opt.getAttribute('data-opt'), yen(opt.__variant.price));
    });

    /* ── 選択状態 ───────────────────────────────────────── */

    function planQty(plan) {
      return parseInt(plan.querySelector('.qsel').getAttribute('data-qty'), 10) || 0;
    }

    function optQty(opt) {
      if (!opt.classList.contains('on')) return 0;
      return parseInt(opt.querySelector('.oqsel').value, 10) || 0;
    }

    /**
     * カートへ入れる行を組む。
     * 合計はこの行の和なので、画面の合計もここから出す（食い違いが起きない形）。
     */
    function buildItems() {
      var items = [];
      var unitPlans = plans.filter(function (p) { return planQty(p) > 0; });

      // 1台目にする1台を決める。実効割引がいちばん大きい種類を選ぶ（先頭コメント参照）
      var firstPlan = null;
      var bestGap = -1;
      unitPlans.forEach(function (plan) {
        var second = plan.__second || plan.__first;
        var gap = Number(plan.__first.price) - Number(second.price);
        if (gap > bestGap) {
          bestGap = gap;
          firstPlan = plan;
        }
      });

      unitPlans.forEach(function (plan) {
        var q = planQty(plan);
        var takesFirst = plan === firstPlan ? 1 : 0;
        var second = plan.__second || plan.__first; // 2台目のバリアントが無ければ割引なし
        if (takesFirst > 0) items.push({ id: plan.__first.id, price: Number(plan.__first.price), quantity: 1 });
        var rest = q - takesFirst;
        if (rest > 0) items.push({ id: second.id, price: Number(second.price), quantity: rest });
      });

      opts.forEach(function (opt) {
        var q = optQty(opt);
        if (q > 0) items.push({ id: opt.__variant.id, price: Number(opt.__variant.price), quantity: q });
      });

      // ★ 同じ variant を2行に分けて送らない ★
      //   2台目以降のバリアントが無いストアでは 1台目のフォールバックを使うため、
      //   「1台目 ×1」と「残り ×(n-1)」が同じ id になる。金額は同じだが、
      //   同一 id を2件送ったときの Shopify 側の畳み方に依存したくない。
      var merged = [];
      var byId = {};
      items.forEach(function (item) {
        var key = String(item.id);
        if (byId[key]) {
          byId[key].quantity += item.quantity;
          return;
        }
        byId[key] = { id: item.id, price: item.price, quantity: item.quantity };
        merged.push(byId[key]);
      });
      return merged;
    }

    /** エアコン本体の洗浄の合計台数。オプションは数えない */
    function unitTotal() {
      return plans.reduce(function (sum, plan) { return sum + planQty(plan); }, 0);
    }

    function render() {
      var html = '';
      var unitCount = 0;
      var unitsFull = 0;

      plans.forEach(function (plan) {
        var q = planQty(plan);
        plan.classList.toggle('on', q > 0);
        var minus = plan.querySelector('.qm');
        if (minus) minus.disabled = q === 0;
        if (q > 0) {
          var full = Number(plan.__first.price) * q;
          unitsFull += full;
          unitCount += q;
          html +=
            '<div class="r"><span>' + plan.querySelector('b').textContent +
            '</span><span>×' + q + '台</span><span>' + yenWithUnit(full) + '</span></div>';
        }
      });

      var items = buildItems();
      var cartTotal = items.reduce(function (sum, it) { return sum + it.price * it.quantity; }, 0);
      var optionsTotal = 0;
      opts.forEach(function (opt) { optionsTotal += Number(opt.__variant.price) * optQty(opt); });
      var unitsCart = cartTotal - optionsTotal;

      // ★ 割引は「定価の合計 − 実際にカートへ入れる合計」から出す ★
      var discount = unitsFull - unitsCart;
      if (discount > 0) {
        html +=
          '<div class="r disc"><span>2台目割引</span><span></span><span>-' +
          yenWithUnit(discount) + '</span></div>';
      }

      opts.forEach(function (opt) {
        var q = optQty(opt);
        if (q === 0) return;
        var line = Number(opt.__variant.price) * q;
        html +=
          '<div class="r"><span>' + opt.querySelector('.t b').textContent +
          '</span><span>×' + q + '台</span><span>' + yenWithUnit(line) + '</span></div>';
      });

      rowsEl.innerHTML = html || '<div class="empty">上からエアコンの種類と台数を選んでください</div>';
      totalEl.textContent = yenWithUnit(Math.max(0, cartTotal));

      var ready = unitCount > 0; // = unitTotal()。ここでは行の描画ついでに数えている
      cbtn.setAttribute('aria-disabled', ready ? 'false' : 'true');

      if (!barLabel || !barPrice || !barBtn) return;
      if (ready) {
        barLabel.textContent = '合計(税込)';
        barPrice.textContent = yenWithUnit(Math.max(0, cartTotal));
        barBtn.textContent = '注文へ進む →';
        barBtn.setAttribute('href', '#kc-cartsec');
      } else {
        barLabel.textContent = 'エアコンクリーニング';
        barPrice.textContent = yenWithUnit(normalPlan.__first.price) + '〜';
        barBtn.textContent = 'エアコンを選ぶ';
        barBtn.setAttribute('href', '#kc-order');
      }
    }

    /* ── 操作 ───────────────────────────────────────────── */

    plans.forEach(function (plan) {
      var sel = plan.querySelector('.qsel');
      function setQty(v) {
        var next = Math.max(0, Math.min(MAX_QTY, v));
        sel.setAttribute('data-qty', String(next));
        sel.textContent = String(next);
        render();
      }
      plan.querySelector('.qp').addEventListener('click', function (event) {
        event.stopPropagation();
        setQty(planQty(plan) + 1);
      });
      plan.querySelector('.qm').addEventListener('click', function (event) {
        event.stopPropagation();
        setQty(planQty(plan) - 1);
      });
      // カードのどこを押しても、0台なら1台にする（渡し版と同じ）
      plan.addEventListener('click', function (event) {
        if (event.target.closest('button')) return;
        if (planQty(plan) === 0) setQty(1);
      });
    });

    opts.forEach(function (opt) {
      function toggle() {
        var on = !opt.classList.contains('on');
        opt.classList.toggle('on', on);
        opt.setAttribute('aria-checked', on ? 'true' : 'false');
        render();
      }
      opt.addEventListener('click', function (event) {
        if (event.target.closest('select')) return;
        toggle();
      });
      // ★ キーボードでも選べるようにする ★
      //   マークアップ側で role="checkbox" / tabindex="0" を持たせている。
      //   Space はページのスクロールを起こすので既定動作を止める。
      opt.addEventListener('keydown', function (event) {
        if (event.target.closest('select')) return;
        if (event.key !== ' ' && event.key !== 'Enter') return;
        event.preventDefault();
        toggle();
      });
      opt.querySelector('.oqsel').addEventListener('change', render);
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        plans.forEach(function (plan) {
          var sel = plan.querySelector('.qsel');
          sel.setAttribute('data-qty', '0');
          sel.textContent = '0';
        });
        opts.forEach(function (opt) {
          opt.classList.remove('on');
          opt.setAttribute('aria-checked', 'false');
        });
        errEl.hidden = true;
        render();
      });
    }

    /* ── 追従バーは「本体のボタンが見えていない間」だけ出す ─────────── */

    /*
     * ★ 商品ページと同じ作りにしている（2026-09-05 日高さん指示）★
     *   渡し版はスマホで出しっぱなしだったが、「この内容で注文へ進む」が
     *   画面にあるのに同じ役目のバーが下に居座るのは重複。
     *   sections/aicon-product-buybox.liquid の `.aicon-buybox__sticky` が
     *   すでに同じことをしているので、判定も rootMargin も**そちらに合わせた**。
     *
     * ★ rootMargin の -72px ★
     *   バー自身の高さぶん下端を削る。削らないと、本体のボタンがバーの裏に
     *   来た瞬間に「見えている」と判定されてバーが消え、少し動かすとまた出る、
     *   というちらつきになる。
     *
     * ★ IntersectionObserver が無い環境では出しっぱなしにする ★
     *   出しすぎるほうが、押せる場所が無くなるより害が小さい（購入ボックスと同じ判断）。
     */
    if (barBtn && typeof IntersectionObserver === 'function') {
      const bar = root.querySelector('#kc-bar');
      if (bar) {
        bar.classList.add('is-hidden'); // 初期表示では本体のボタンが見えている
        const barObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              bar.classList.toggle('is-hidden', entry.isIntersecting);
            });
          },
          { rootMargin: '0px 0px -72px 0px' },
        );
        barObserver.observe(cbtn);
      }
    }

    /* ── 同意モーダル ───────────────────────────────────── */

    var lastFocused = null;

    function openModal() {
      if (!modal) return;
      lastFocused = document.activeElement;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      if (agreeBtn) agreeBtn.focus();
    }

    function closeModal() {
      if (!modal) return;
      modal.hidden = true;
      document.body.style.overflow = '';
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    cbtn.addEventListener('click', function () {
      if (cbtn.getAttribute('aria-disabled') === 'true') return;
      openModal();
    });
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (modal) {
      modal.addEventListener('click', function (event) {
        if (event.target === modal) closeModal();
      });
    }
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && modal && !modal.hidden) closeModal();
    });

    /* ── カート投入 ─────────────────────────────────────── */

    if (agreeBtn) {
      agreeBtn.addEventListener('click', async function () {
        if (agreeBtn.getAttribute('aria-busy') === 'true') return;
        /*
         * ★ 「洗浄0台＋オプションだけ」を送らせない（2026-09-04）★
         *   オプションは単体では意味の無い加算で、これだけの注文が通ると
         *   **エアコンを1台も洗わないのに 5,500円 を請求する**ことになる。
         *   `items.length` だけの判定では素通りする（オプションも items に入るため）。
         *   モーダルにはフォーカストラップが無く、開いたまま Tab で背後の台数を
         *   0 に戻して戻ってくる経路が実際にある。
         */
        if (unitTotal() === 0) return;
        var items = buildItems();
        if (items.length === 0) return;

        agreeBtn.setAttribute('aria-busy', 'true');
        errEl.hidden = true;
        try {
          var res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: items.map(function (it) { return { id: it.id, quantity: it.quantity }; }),
            }),
          });
          if (!res.ok) throw new Error('status=' + res.status);
          window.location.href = root.getAttribute('data-cart-url') || '/cart';
        } catch (err) {
          console.error('[cleaning] カート投入に失敗しました:', err);
          agreeBtn.removeAttribute('aria-busy');
          closeModal();
          errEl.textContent = 'カートに入れられませんでした。時間をおいてお試しください。';
          errEl.hidden = false;
        }
      });
    }

    render();
  }

  function boot() {
    var roots = document.querySelectorAll('[data-section="cleaning-order"]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
